import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  SortableContextProps,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { TableContainer } from '@shared/components'
import { PaginationTypes } from '@shared/hooks'
import { TableProps } from 'antd'

import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { DragHandle, TableRow } from './widgets'

type Props<T> = {
  items: SortableContextProps['items']
  data?: PaginationTypes<T[]>
  rightChildren?: ReactNode
  onDragEnd?: (fn: (state: T[]) => T[]) => void
  /** Drops the drag handle and ignores drops, for read-only viewers. */
  disabled?: boolean
} & TableProps<T>

export function DndTable<T>({
  items,
  columns = [],
  rowKey,
  onDragEnd,
  disabled,
  ...props
}: Props<T>) {
  const { t } = useTranslation()

  const getKey = (record: T, index?: number) => {
    let key = '' as keyof T
    if (typeof rowKey === 'function') {
      key = rowKey(record, index) as keyof T
    } else if (typeof rowKey === 'string') {
      key = rowKey as keyof T
    }

    return key
  }

  const onDragEndHandler = ({ active, over }: DragEndEvent) => {
    if (disabled) return

    if (active.id !== over?.id) {
      onDragEnd?.((state) => {
        const activeIndex = state.findIndex(
          (record, index) => record?.[getKey(record, index)] === active?.id
        )
        const overIndex = state.findIndex(
          (record, index) => record?.[getKey(record, index)] === over?.id
        )

        return arrayMove(state, activeIndex, overIndex)
      })
    }
  }

  return (
    <DndContext
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onDragEndHandler}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <TableContainer
          searchPlaceholder={t('search')}
          showSorterTooltip={false}
          components={{ body: { row: TableRow } }}
          columns={[
            ...(disabled
              ? []
              : [
                  {
                    key: 'sort',
                    align: 'center' as const,
                    width: 30,
                    render: () => <DragHandle />
                  }
                ]),

            ...columns
          ]}
          {...props}
        />
      </SortableContext>
    </DndContext>
  )
}
